import { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, SearchSm } from "@untitledui/icons"

import { TaskChip } from "./task-chip"
import type { FollowUpCadence, TaskGroup, TaskRow } from "@/lib/api/tasks"
import { KIND_TABS, TASK_KIND, kindOf, lastHappened, matchesKindTab, type KindTab } from "@/lib/tasks/task-types"
import { dueLine } from "@/lib/tasks/task-copy"
import { cx } from "@/lib/utils/cx"

// The day's list — a column that scrolls on its own, beside the workspace.
// Working it top to bottom is the whole job, so the order IS the instruction:
// overdue, then now, then later today. Every row leads with its kind chip, so
// a call and a reply are different at a glance; the tabs let her batch one
// kind; the find box is for "where's Sandra?"; up/down walk the list; the
// selected row always stays in view.

const SECTIONS: { key: TaskGroup; label: string; collapsed?: boolean }[] = [
  { key: "overdue", label: "Overdue" },
  { key: "now", label: "Now" },
  { key: "later", label: "Later today" },
  { key: "upcoming", label: "Upcoming", collapsed: true },
  { key: "done", label: "Done today", collapsed: true },
]

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`
}

function matchesTerm(r: TaskRow, term: string): boolean {
  if (!term) return true
  if (r.customerName.toLowerCase().includes(term)) return true
  if (r.vehicleLabel.toLowerCase().includes(term)) return true
  const digits = term.replace(/\D/g, "")
  if (digits.length >= 3 && (r.phone ?? "").replace(/\D/g, "").includes(digits)) return true
  return false
}

const FIELD_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"])

interface TaskListProps {
  /** ALL rows — the list filters by tab and counts every tab. */
  rows: TaskRow[]
  cadence: FollowUpCadence | null
  tab: KindTab
  onTab: (t: KindTab) => void
  selectedId: number | null
  onSelect: (row: TaskRow) => void
  className?: string
}

export function TaskList({ rows, cadence, tab, onTab, selectedId, onSelect, className }: TaskListProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [q, setQ] = useState("")
  const listRef = useRef<HTMLDivElement>(null)

  const term = q.trim().toLowerCase()
  const shown = useMemo(() => rows.filter((r) => matchesKindTab(r, tab)), [rows, tab])

  const isCollapsed = (s: (typeof SECTIONS)[number]) =>
    term ? false : open[s.key] === undefined ? !!s.collapsed : !open[s.key]

  const visible = useMemo(() => {
    const out: TaskRow[] = []
    for (const s of SECTIONS) {
      if (isCollapsed(s)) continue
      out.push(...shown.filter((r) => r.group === s.key && matchesTerm(r, term)))
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, term, open])

  // Tab counts are over the work, not the done pile.
  const tabCounts = useMemo(() => {
    const live = rows.filter((r) => r.group !== "done")
    return Object.fromEntries(KIND_TABS.map((t) => [t.key, live.filter((r) => matchesKindTab(r, t.key)).length])) as Record<KindTab, number>
  }, [rows])

  useEffect(() => {
    if (selectedId == null) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-task-id="${selectedId}"]`)
    el?.scrollIntoView({ block: "nearest" })
  }, [selectedId])

  const currentRef = useRef<number | null>(selectedId)
  useEffect(() => {
    currentRef.current = selectedId
  }, [selectedId])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
      const t = e.target as HTMLElement | null
      if (t && (FIELD_TAGS.has(t.tagName) || t.isContentEditable)) return
      if (visible.length === 0) return
      const idx = visible.findIndex((r) => r.id === currentRef.current)
      const next = e.key === "ArrowDown" ? visible[Math.min(idx + 1, visible.length - 1)] : visible[Math.max(idx - 1, 0)]
      if (next && next.id !== currentRef.current) {
        e.preventDefault()
        currentRef.current = next.id
        onSelect(next)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [visible, onSelect])

  const matchCount = shown.filter((r) => matchesTerm(r, term)).length

  return (
    <div className={cx("rounded-2xl border border-zinc-200/80 bg-white shadow-sm flex flex-col min-h-0", className)}>
      <div className="shrink-0 px-3 pt-3 pb-2 border-b border-zinc-100 space-y-2">
        <label className="relative block">
          <SearchSm className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a customer…"
            aria-label="Find a task by customer, vehicle or phone"
            className="w-full h-10 pl-8 pr-3 rounded-lg border border-zinc-300 bg-white text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 transition-[border-color,box-shadow] duration-150 ease-out-quart motion-reduce:transition-none"
          />
        </label>
        {/* Kind tabs — batch one kind of work. A zero still renders: she sees the pile is empty. */}
        <div className="flex items-center gap-1" role="tablist" aria-label="Task kind">
          {KIND_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => onTab(t.key)}
              className={cx(
                "h-9 px-2.5 rounded-lg text-[13px] font-medium tabular-nums transition-colors duration-150 ease-out-quart motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20",
                tab === t.key ? "bg-zinc-100 text-zinc-900 font-semibold" : "text-zinc-600 hover:text-zinc-900",
              )}
            >
              {t.label} <span className="text-zinc-500">{tabCounts[t.key]}</span>
            </button>
          ))}
        </div>
        {term && (
          <p className="px-1 text-[12.5px] text-zinc-500 tabular-nums">
            {matchCount} {matchCount === 1 ? "match" : "matches"}
            <span className="text-zinc-300 mx-1.5">·</span>
            <button type="button" onClick={() => setQ("")} className="font-semibold text-zinc-600 hover:text-zinc-900">
              clear
            </button>
          </p>
        )}
      </div>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {shown.length === 0 && !term && (
          <p className="px-4 py-8 text-center text-[13.5px] text-zinc-500">Nothing in this pile.</p>
        )}
        {term && matchCount === 0 && (
          <p className="px-4 py-8 text-center text-[13.5px] text-zinc-500">Nobody matches "{q}".</p>
        )}
        {SECTIONS.map((s) => {
          const items = shown.filter((r) => r.group === s.key && matchesTerm(r, term))
          if (items.length === 0) return null
          const collapsed = isCollapsed(s)
          return (
            <section key={s.key} className="border-b border-zinc-100 last:border-b-0">
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [s.key]: collapsed }))}
                className="sticky top-0 z-10 w-full flex items-center justify-between px-3.5 h-9 bg-white/95 backdrop-blur text-left border-b border-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/20"
              >
                <span
                  className={cx(
                    "text-[11px] font-bold uppercase tracking-[0.12em]",
                    s.key === "overdue" ? "text-red-600" : s.key === "now" ? "text-zinc-900" : "text-zinc-500",
                  )}
                >
                  {s.label}
                  <span className="ml-1.5 tabular-nums font-semibold opacity-60">{items.length}</span>
                </span>
                <ChevronDown className={cx("size-3.5 text-zinc-500 transition-transform duration-200 ease-out-quart motion-reduce:transition-none", collapsed && "-rotate-90")} aria-hidden />
              </button>
              {!collapsed && (
                <ul className="divide-y divide-zinc-50">
                  {items.map((r) => {
                    const active = r.id === selectedId
                    const due = dueLine(r)
                    const kind = kindOf(r.type)
                    const done = r.group === "done"
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          data-task-id={r.id}
                          onClick={() => onSelect(r)}
                          className={cx(
                            "w-full text-left pl-3 pr-3.5 py-3 border-l-2 transition-[background-color,border-color] duration-150 ease-out-quart motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/20",
                            active ? "bg-zinc-50 border-l-zinc-900" : "border-l-transparent hover:bg-zinc-50/70",
                            done && "opacity-55",
                          )}
                        >
                          <div className="flex items-start gap-2.5">
                            <TaskChip kind={kind} className="mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <p className="text-[14px] font-semibold text-zinc-900 truncate leading-tight">{r.customerName}</p>
                                <span className="text-[13px] font-semibold text-zinc-900 tabular-nums shrink-0">{money(r.avg)}</span>
                              </div>
                              <div className="flex items-baseline justify-between gap-2 mt-0.5">
                                <p className="text-[13px] text-zinc-500 truncate leading-tight">
                                  {(kind === "call" && r.type !== "callback" && lastHappened(r)) ||
                                    TASK_KIND[kind].subLabel(r, cadence) ||
                                    (kind === "visit" ? "Did they come in?" : kind === "fix" ? "No working number" : "")}
                                </p>
                                <p
                                  className={cx(
                                    "text-[12.5px] font-medium shrink-0 leading-tight inline-flex items-center gap-1",
                                    !done && due.tone === "overdue" && "text-red-600",
                                    !done && due.tone === "now" && "text-amber-700",
                                    (done || due.tone === "quiet") && "text-zinc-500",
                                  )}
                                >
                                  {done && <Check className="size-3 text-emerald-600" aria-hidden />}
                                  {due.text}
                                </p>
                              </div>
                            </div>
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}
